import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Warning, CreditCard, UploadSimple, TrendUp, CalendarCheck, CheckCircle } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const STATUS_COLORS = { draft: "#94a3b8", in_review: "#f59e0b", filed: "#0A2540", accepted: "#16a34a", rejected: "#D32F2F" };
const TOOLTIP_STYLE = { borderRadius: 6, border: "1px solid #e2e8f0" };
const BAR_RADIUS = [4, 4, 0, 0];
const LEGEND_STYLE = { fontSize: 11 };

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function daysUntilFilingDay() {
  const now = new Date();
  const y = now.getMonth() > 3 || (now.getMonth() === 3 && now.getDate() > 15) ? now.getFullYear() + 1 : now.getFullYear();
  const deadline = new Date(y, 3, 15); // April 15
  const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  return { days: diff, year: y };
}

function StatCard({ label, value, unit, hint, icon: Icon, accent }) {
  return (
    <div className="rounded-md border tf-hairline bg-white p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-[#0A2540]">
            {value}{unit && <span className="text-lg text-slate-400 ml-1">{unit}</span>}
          </div>
          {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
        <div className={`h-9 w-9 rounded grid place-items-center ${accent || "bg-[#0A2540]/5 text-[#0A2540]"}`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [recent, setRecent] = useState([]);
  const [notices, setNotices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/analytics/monthly-returns").then(({ data }) => setMonthly(data)).catch(() => {});
    api.get("/returns").then(({ data }) => setRecent(data)).catch(() => {});
    api.get("/notices").then(({ data }) => setNotices(data)).catch(() => {});
    api.get("/documents").then(({ data }) => setDocuments(data)).catch(() => {});
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  }, []);

  const statusData = stats ? Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: k, value: v })) : [];
  const isClient = user?.role === "client";
  const { days: daysLeft, year: filingYear } = daysUntilFilingDay();

  const nextActions = useMemo(() => {
    const items = [];
    const openNotices = (notices || []).filter((n) => n.status !== "resolved");
    const draftReturns = (recent || []).filter((r) => r.status === "draft");
    const inReview = (recent || []).filter((r) => r.status === "in_review");
    const unpaidInvoices = (invoices || []).filter((i) => (i.status || i.payment_status) !== "paid");
    const noDocs = (documents || []).length === 0;

    if (openNotices.length) items.push({ key: "notices", text: `Respond to ${openNotices.length} open IRS notice${openNotices.length > 1 ? "s" : ""}`, to: "/dashboard/notices" });
    if (draftReturns.length) items.push({ key: "drafts", text: `${draftReturns.length} draft return${draftReturns.length > 1 ? "s" : ""} awaiting ${isClient ? "review" : "preparation"}`, to: "/dashboard/returns" });
    if (isClient && noDocs) items.push({ key: "upload", text: "Upload your W-2 and 1099s to get started", to: "/dashboard/documents" });
    if (isClient && unpaidInvoices.length) items.push({ key: "pay", text: `${unpaidInvoices.length} invoice${unpaidInvoices.length > 1 ? "s" : ""} awaiting payment`, to: "/dashboard/invoices" });
    if (!isClient && inReview.length) items.push({ key: "review", text: `${inReview.length} return${inReview.length > 1 ? "s" : ""} queued for your review`, to: "/dashboard/returns" });
    if (daysLeft > 0 && daysLeft <= 60) items.push({ key: "deadline", text: `${daysLeft} days left until the ${filingYear} filing deadline`, to: "/dashboard/returns" });
    return items.slice(0, 4);
  }, [notices, recent, documents, invoices, isClient, daysLeft, filingYear]);

  const recentRows = (recent || []).slice(0, 5);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Mission Control</div>
          <h1 className="mt-2 font-[Chivo] text-4xl font-black text-[#0A2540]" data-testid="overview-title">
            {greeting()}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="mt-2 text-slate-600" data-testid="overview-subtitle">
            {isClient
              ? "Track your filings, notices, and payments in one place."
              : "Here's the live pulse of your tax operations."}
          </p>
        </div>
        {daysLeft > 0 && daysLeft <= 120 && (
          <div className="rounded-md border tf-hairline bg-white px-4 py-3 flex items-center gap-3" data-testid="filing-countdown">
            <div className="h-9 w-9 rounded grid place-items-center bg-[#D32F2F]/10 text-[#D32F2F]"><CalendarCheck size={18} weight="duotone" /></div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Filing deadline</div>
              <div className="font-[Chivo] font-bold text-[#0A2540]">{daysLeft} days left · Apr 15, {filingYear}</div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Returns" value={stats?.total_returns ?? "—"} icon={FileText} />
        <StatCard label="Open IRS Notices" value={stats?.open_notices ?? "—"} icon={Warning} accent="bg-[#D32F2F]/10 text-[#D32F2F]" />
        <StatCard label="Payments" value={stats?.payments_count ?? "—"} icon={CreditCard} />
        <StatCard label="Total Refunds" value={stats ? `$${(stats.total_refund || 0).toLocaleString()}` : "—"} icon={TrendUp} accent="bg-emerald-50 text-emerald-700" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-md border tf-hairline bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-[Chivo] font-bold text-[#0A2540]">Filings by month</h3>
            <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">Last 12 months</span>
          </div>
          <div className="mt-4 h-64" data-testid="chart-monthly">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="returns" fill="#0A2540" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <h3 className="font-[Chivo] font-bold text-[#0A2540]">Returns by status</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                  {statusData.map((entry) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend iconSize={8} wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0 rounded-md border tf-hairline bg-white">
          <div className="p-5 border-b tf-hairline flex items-center justify-between">
            <h3 className="font-[Chivo] font-bold text-[#0A2540]">Recent returns</h3>
            <Link to="/dashboard/returns" className="text-xs text-[#D32F2F] font-semibold inline-flex items-center gap-1" data-testid="recent-returns-link">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="p-2 overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left p-3">Year</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Liability</th>
                  <th className="text-right p-3">Refund</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-500">No returns yet.</td></tr>
                )}
                {recentRows.map((r) => (
                  <tr key={r.id} className="border-t tf-hairline hover:bg-slate-50">
                    <td className="p-3 font-mono">{r.tax_year}</td>
                    <td className="p-3">{r.return_type}</td>
                    <td className="p-3">
                      <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${STATUS_COLORS[r.status]}20`, color: STATUS_COLORS[r.status] }}>
                        {r.status}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono">${Number(r.tax_liability || 0).toLocaleString()}</td>
                    <td className="p-3 text-right font-mono text-emerald-700">${Number(r.refund_amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-md border tf-hairline bg-[#0A2540] text-white p-6" data-testid="next-best-actions">
          <h3 className="font-[Chivo] font-bold">Next best actions</h3>
          {nextActions.length === 0 ? (
            <div className="mt-6 flex flex-col items-center text-center gap-3 opacity-90">
              <CheckCircle size={32} weight="duotone" className="text-emerald-300" />
              <p className="text-sm">You're all caught up. No pending items.</p>
            </div>
          ) : (
            <ul className="mt-4 space-y-3 text-sm">
              {nextActions.map((item, idx) => (
                <li
                  key={item.key}
                  className={`flex items-start gap-2 ${idx < nextActions.length - 1 ? "border-b border-white/10 pb-3" : ""}`}
                  data-testid={`action-${item.key}`}
                >
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D32F2F] shrink-0" />
                  <Link to={item.to} className="hover:underline">{item.text}</Link>
                </li>
              ))}
            </ul>
          )}
          <Link to={isClient ? "/dashboard/documents" : "/dashboard/returns"}>
            <Button className="mt-6 w-full bg-white text-[#0A2540] hover:bg-slate-100" data-testid="upload-cta">
              <UploadSimple size={16} className="mr-2" /> {isClient ? "Upload documents" : "Open returns board"}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
